import type { SigningLane } from './signingChannel'

export const SIGNATURE_REQUESTED_TITLE = 'Signature Requested'

export interface NextStepParagraph {
  /** Bold lead sentence shown as a heading inside the paragraph. */
  heading: string
  /** Supporting body text following the heading. */
  body: string
}

/**
 * Next Steps for a request another clinician signs in the app. DocuSign never
 * mails an embedded recipient, so nothing here may mention a DocuSign email.
 * Replaces tenant orderSuccess.bodyHtml in the Signature Requested modal.
 */
export const EMBEDDED_OTHER_NEXT_STEPS_PARAGRAPHS: NextStepParagraph[] = [
  {
    heading: 'Make sure the authorizing clinician signs the requisition form.',
    body: "We've emailed them a link to sign it in Inflection. The lab requires a signed form for every order.",
  },
  {
    heading: 'Once signed, the form is saved to the order and sent on automatically.',
    body: 'Nothing to download or forward.',
  },
  {
    heading: 'Changes that affect the requisition form need a new signature.',
    body: 'The previous request is voided automatically and the clinician signs the updated form in Inflection.',
  },
]

export interface SignatureRequestedCopy {
  title: string
  subtitle: string
  nextSteps: NextStepParagraph[]
}

export interface OrderSuccessSigningInput {
  lane: SigningLane
  /**
   * Display name for the signer (e.g. "Dr. Smith"). Null or whitespace-only
   * produces the nameless subtitle form.
   */
  clinicianName: string | null
}

const signerName = (clinicianName: string | null): string => clinicianName?.trim() || 'the authorizing clinician'

/**
 * Title, subtitle and Next Steps for the Signature Requested success modal when
 * someone else signs and is emailed a link. Returns null when the modal should
 * keep tenant orderSuccess copy (DocuSign off) or never opens (a self-submit,
 * which goes straight to signing).
 */
export function orderSuccessSigningCopy(input: OrderSuccessSigningInput): SignatureRequestedCopy | null {
  switch (input.lane) {
    case 'embedded-other':
      return {
        title: SIGNATURE_REQUESTED_TITLE,
        subtitle: `We've emailed ${signerName(input.clinicianName)} a link to sign the requisition form in Inflection.`,
        nextSteps: EMBEDDED_OTHER_NEXT_STEPS_PARAGRAPHS,
      }
    case 'ehr':
    case 'embedded':
      return null
  }
}
